import { Alert, Button, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Image as ZhImage,
    ImageViewer as ZhImageViewer
} from "zhihao-ui-react";

import request from "@/api";
import { fileService } from "@/api/services/fileService";
import { DEFAULT_VALUE, LOG_DATE_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { useUserStore } from "@/views/stores";
import DetailJson, { type DetailJsonBlock } from "@/views/components/detailJson";
import type { MessageFeedback } from "@zhihaoscm/entity";
import { MESSAGE_FEEDBACK_STATE, MESSAGE_FEEDBACK_TYPE } from "@zhihaoscm/meta";

export interface MessageFeedbackDetailPageProps {
    loadDetail?: (id: number | string) => Promise<MessageFeedback>;
    replyFeedback?: (id: number, payload: { replyContent: string }) => Promise<unknown>;
    permissions?: string[];
}

/** 沿用 Vue MessageFeedbackService.get 的留言反馈详情接口。 */
export async function loadMessageFeedbackDetail(id: number | string): Promise<MessageFeedback> {
    const response = await request.service.get<MessageFeedback>(`/admin/message-feedback/${id}`);
    return response.data;
}

/** 沿用 Vue 回复留言反馈接口及 replyContent 请求体。 */
export async function replyMessageFeedbackDetail(id: number, payload: { replyContent: string }): Promise<unknown> {
    const response = await request.service.put(`/admin/message-feedback/reply/${id}`, payload);
    return response.data;
}

/** 将 Vue 客户 JSON 信息拼接为详情页反馈账号文案。 */
function getCustomerLabel(detail: MessageFeedback): string {
    const customer = detail.commenterEnterprise;
    return (
        [customer?.institutionName || customer?.realName, customer?.mobile, customer?.code, customer?.appTypeName]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

/** 保持旧 Vue 详情页的日志时间字段格式。 */
function formatDetailTime(value?: string): string {
    return value ? dateFormat(value, LOG_DATE_FORMAT) : DEFAULT_VALUE;
}

/** 用户留言反馈详情页，保留 Vue 详情字段、回复入口和权限契约。 */
export function MessageFeedbackDetailPage({
    loadDetail = loadMessageFeedbackDetail,
    replyFeedback = replyMessageFeedbackDetail,
    permissions
}: MessageFeedbackDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，留言详情回复入口保留但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const id = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<MessageFeedback | null>(null);
    const [pictures, setPictures] = useState<Array<{ id: number; path: string }>>([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [replyVisible, setReplyVisible] = useState(false);
    const [replyContent, setReplyContent] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const canReply = currentPermissions.includes("ROLE_SS_FEEDBACK_W");
    // 预览器与缩略图共用已解析的反馈图片地址，保持旧 Vue 的多图顺序一致。
    const previewImageUrls = pictures.map(picture => picture.path);
    const detailColumns: DetailJsonBlock[] = [
        {
            sectionTitle: "基本信息",
            list: [
                { label: "反馈账号", span: 2, value: () => (detail ? getCustomerLabel(detail) : DEFAULT_VALUE) },
                {
                    label: "留言类型",
                    value: () => MESSAGE_FEEDBACK_TYPE.getIntroByCode(Number(detail?.type)) || DEFAULT_VALUE
                },
                { label: "反馈内容", span: 4, value: () => detail?.content || DEFAULT_VALUE },
                { label: "反馈图片", slot: "pictureSlot", span: 4, show: pictures.length > 0 }
            ]
        },
        {
            sectionTitle: "回复信息",
            show: Number(detail?.state) === 1,
            list: [
                { label: "回复人", span: 4, value: () => detail?.respondentName || DEFAULT_VALUE },
                { label: "回复时间", span: 4, value: () => formatDetailTime(detail?.replyTime) },
                { label: "回复内容", span: 4, value: () => detail?.replyContent || DEFAULT_VALUE }
            ]
        }
    ];

    /** 打开 Vue 回复弹窗时清空上一次的编辑内容。 */
    const openReplyDialog = () => {
        setReplyContent("");
        setErrorMessage(null);
        setReplyVisible(true);
    };

    /** 提交回复并刷新详情，确保页面同步显示最新回复信息。 */
    const submitReply = async () => {
        if (!id || !replyContent.trim()) {
            setErrorMessage("请输入回复内容");
            return;
        }
        setSubmitting(true);
        try {
            await replyFeedback(Number(id), { replyContent: replyContent.trim() });
            setReplyVisible(false);
            setReplyContent("");
            setErrorMessage(null);
            const nextDetail = await loadDetail(id);
            const nextPictures = nextDetail.pics?.length ? await fileService.getFileByIds(nextDetail.pics) : [];
            setDetail(nextDetail);
            setPictures(nextPictures.map(file => ({ id: Number(file.id), path: file.path })));
        } catch {
            setErrorMessage("留言回复失败");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!id) {
            setErrorMessage("缺少留言反馈编号");
            return;
        }
        let disposed = false;
        void loadDetail(id)
            .then(async result => {
                const files = result.pics?.length ? await fileService.getFileByIds(result.pics) : [];
                if (!disposed) {
                    setDetail(result);
                    setPictures(files.map(file => ({ id: Number(file.id), path: file.path })));
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户留言反馈详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    return (
        <section
            aria-label="message-feedback-detail-page"
            className="detail-json-page detail-page-transparent admin-detail-page"
        >
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: detail?.id ?? DEFAULT_VALUE }]}
                    rightInfos={[{ label: "留言时间", value: formatDetailTime(detail?.createdTime) }]}
                    stateDesc={MESSAGE_FEEDBACK_STATE.getIntroByCode(Number(detail?.state)) || DEFAULT_VALUE}
                    title="用户留言反馈详情"
                />
            </div>
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {detail ? (
                <DetailJson
                    columns={detailColumns}
                    slots={{
                        pictureSlot: () => (
                            <Space wrap>
                                {pictures.map((picture, index) => (
                                    <button
                                        key={picture.id}
                                        aria-label={`预览反馈图片 ${index + 1}`}
                                        className="cursor-pointer border-0 bg-transparent p-0"
                                        onClick={() => {
                                            // 对齐旧 Vue 图片查看器，点击缩略图从当前图片开始预览全部反馈图片。
                                            setPreviewIndex(index);
                                            setPreviewVisible(true);
                                        }}
                                        type="button"
                                    >
                                        <ZhImage
                                            alt={`反馈图片 ${index + 1}`}
                                            height="135"
                                            preview={false}
                                            src={picture.path}
                                            width="240"
                                        />
                                    </button>
                                ))}
                            </Space>
                        )
                    }}
                />
            ) : null}
            <Space className="operation-fixed-btn" size={0}>
                {detail && Number(detail.state) === 0 && canReply ? (
                    <Button aria-label="回复" disabled={banButton} onClick={openReplyDialog} type="primary">
                        回复
                    </Button>
                ) : null}
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
            {previewVisible && pictures.length ? (
                <ZhImageViewer
                    alt="反馈图片预览"
                    initialIndex={previewIndex}
                    onClose={() => setPreviewVisible(false)}
                    urlList={previewImageUrls}
                />
            ) : null}
            <ZhDialog
                alignCenter
                modalClass="user-operation-reply-dialog"
                open={replyVisible}
                title="回复留言反馈"
                width={493}
                closeOnClickModal={false}
                closeOnPressEscape={false}
                onCancel={() => setReplyVisible(false)}
                footer={
                    <>
                        <Button aria-label="取消" onClick={() => setReplyVisible(false)}>
                            取消
                        </Button>
                        <Button
                            aria-label="确认回复"
                            loading={submitting}
                            onClick={() => void submitReply()}
                            type="primary"
                        >
                            确认
                        </Button>
                    </>
                }
            >
                <div className="user-operation-action-dialog-form">
                    <label className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--reply">
                        <span className="user-operation-action-dialog-form-label">
                            <span className="user-operation-action-dialog-required">*</span>
                            回复内容
                        </span>
                        <span className="user-operation-action-dialog-form-control">
                            <Input.TextArea
                                aria-label="回复内容"
                                className="user-operation-action-dialog-textarea"
                                maxLength={200}
                                placeholder="请输入内容"
                                rows={2}
                                showCount
                                value={replyContent}
                                onChange={event => setReplyContent(event.target.value)}
                            />
                        </span>
                    </label>
                </div>
            </ZhDialog>
        </section>
    );
}

export default MessageFeedbackDetailPage;
